import React, { useState } from 'react';
import { LuWrench } from 'react-icons/lu';
import { 
  IoCalendarOutline, 
  IoTimeOutline, 
  IoChevronDown, 
  IoCloseOutline 
} from 'react-icons/io5';
import { FiPlus, FiSave } from 'react-icons/fi';

export default function CreateEquipmentFailureModal({ onClose }) {
  const [formData, setFormData] = useState({
    location: '',
    date: '2025-05-28',
    time: '10:20',
    equipment: '',
    description: '',
    impact: '',
    immediateAction: '',
  });

  const [images] = useState([
    'https://unsplash.com' // Equipment placeholder
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting Equipment Failure Incident:', formData);
    onClose();
  };

  return (
    <div className="w-full bg-white font-sans rounded-xl text-slate-800">
      {/* Header section */}
      <div className="p-5 pb-4 border-b border-slate-100 flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
            <LuWrench className="text-xl" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Create Equipment Failure</h2>
            <p className="text-xs text-slate-400 mt-0.5">Record details of equipment failure or malfunction.</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
          <IoCloseOutline className="text-2xl" />
        </button>
      </div>

      {/* Form Body */}
      <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
        
        {/* Row: Location & Date/Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Location */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Location <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500 text-slate-600"
              >
                <option value="">Select location</option>
                <option value="Main Kitchen">Main Kitchen</option>
                <option value="Bar Area">Bar Area</option>
                <option value="Server Station">Server Station</option>
                <option value="Walk-in Cooler">Walk-in Cooler</option>
              </select>
              <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Date & Time <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full text-sm border border-slate-200 rounded-xl pl-3 pr-8 py-2.5 focus:outline-none focus:border-blue-500"
                />
                <IoCalendarOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
              </div>
              <div className="relative w-28">
                <input
                  type="text"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="w-full text-sm border border-slate-200 rounded-xl pl-3 pr-7 py-2.5 focus:outline-none focus:border-blue-500"
                />
                <IoTimeOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Select */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">
            Equipment <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              name="equipment"
              value={formData.equipment}
              onChange={handleChange}
              required
              className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500"
            >
              <option value="">Select equipment</option>
              <option value="POS Terminal 1">POS Terminal 1</option>
              <option value="Walk-in Refrigerator">Walk-in Refrigerator</option>
              <option value="Commercial Dishwasher">Commercial Dishwasher</option>
              <option value="Deep Fryer B">Deep Fryer B</option>
            </select>
            <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Describe the issue with the equipment."
            className="w-full text-sm border border-slate-200 rounded-xl p-3 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 resize-none leading-relaxed"
          ></textarea>
        </div>

        {/* Impact Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Impact</label>
          <input
            type="text"
            name="impact"
            value={formData.impact}
            onChange={handleChange}
            placeholder="How did this impact operations?"
            className="w-full text-sm border border-slate-200 rounded-xl p-3 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Immediate Action Taken Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Immediate Action Taken</label>
          <input
            type="text"
            name="immediateAction"
            value={formData.immediateAction}
            onChange={handleChange}
            placeholder="What action was taken?"
            className="w-full text-sm border border-slate-200 rounded-xl p-3 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Photos / Evidence */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-700">Photos / Evidence</label>
          <p className="text-[11px] text-slate-400">Add photos if available</p>
          
          <div className="flex flex-wrap gap-3 mt-2">
            {images.map((src, i) => (
              <div key={i} className="w-24 h-24 rounded-xl overflow-hidden border border-slate-100 shadow-sm relative">
                <img src={src} alt="Evidence" className="w-full h-full object-cover" />
              </div>
            ))}
            <button
              type="button"
              className="w-24 h-24 border border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors bg-slate-50/50"
            >
              <FiPlus className="text-lg" />
              <span className="text-[11px] font-medium">Add more</span>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
          >
            <FiSave className="text-base" />
            Save Incident
          </button>
        </div>

      </form>
    </div>
  );
}
